import { useEffect, useRef, useState } from "react";
import { Music, Play, Pause } from "lucide-react";

const TRACKS = [
  { id: "ambient", name: "Calm Ambient", src: "/audio/ambient.mp3" },
  { id: "lofi", name: "Soft Guitar · Lo-fi", src: "/audio/lofi.mp3" },
  { id: "nature", name: "Nature Sounds", src: "/audio/nature.mp3" },
];

export default function MusicPlayer() {
  const audioRef = useRef(null);
  const [enabled, setEnabled] = useState(() => localStorage.getItem("bg-music-off") !== "1");
  const [trackId, setTrackId] = useState(() => localStorage.getItem("bg-music-track") || "ambient");
  const [playing, setPlaying] = useState(false);
  const [open, setOpen] = useState(false);

  const track = TRACKS.find((t) => t.id === trackId) || TRACKS[0];

  const tryPlay = () => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.3;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (enabled) {
      tryPlay();
      const onFirstInteraction = () => {
        if (audioRef.current && audioRef.current.paused && localStorage.getItem("bg-music-off") !== "1") {
          tryPlay();
        }
      };
      document.addEventListener("pointerdown", onFirstInteraction, { once: true });
      return () => document.removeEventListener("pointerdown", onFirstInteraction);
    }
    a.pause();
    setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, trackId]);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem("bg-music-off", next ? "0" : "1");
  };

  const pickTrack = (id) => {
    setTrackId(id);
    localStorage.setItem("bg-music-track", id);
    if (!enabled) {
      setEnabled(true);
      localStorage.setItem("bg-music-off", "0");
    }
  };

  return (
    <>
      <audio ref={audioRef} src={track.src} loop preload="none" />
      <div className="fixed left-6 bottom-24 z-40">
        {open && (
          <div
            data-testid="music-panel"
            className="mb-3 w-56 bg-white border border-[#E5E2D9] rounded-2xl shadow-lg p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest text-[#5C665A]">Ambience</span>
              <button
                data-testid="music-onoff-btn"
                onClick={toggle}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                  enabled && playing
                    ? "bg-[#89937C] text-white"
                    : "bg-[#F7F5F0] text-[#5C665A] border border-[#E5E2D9]"
                }`}
              >
                {enabled && playing ? <Pause size={12} /> : <Play size={12} />}
                {enabled && playing ? "On" : "Off"}
              </button>
            </div>
            <div className="space-y-1.5">
              {TRACKS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`music-track-${t.id}`}
                  onClick={() => pickTrack(t.id)}
                  className={`w-full text-left text-sm px-3 py-2 rounded-xl transition-colors ${
                    trackId === t.id
                      ? "bg-[#89937C]/15 text-[#2D372B] font-medium"
                      : "text-[#5C665A] hover:bg-[#F7F5F0]"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <button
          data-testid="music-toggle-button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Background music"
          className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-colors ${
            enabled && playing ? "bg-[#89937C] text-white" : "bg-white text-[#5C665A] border border-[#E5E2D9]"
          }`}
        >
          <Music size={18} className={enabled && playing ? "animate-pulse" : ""} />
        </button>
      </div>
    </>
  );
}
